if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();

        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper') || document.querySelector('sticky-header');

        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };

        // Always safe to bind these; methods are defensive
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));

        // Dropdown toggle (only if markup exists)
        if (this.elements.button) {
          this.elements.button.addEventListener('click', this.openSelector.bind(this));
        }

        // Search + filters (only if markup exists)
        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }

        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }

        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        // Items in dropdown usually have data-value. Flags-only buttons usually don't.
        this.querySelectorAll('[data-value]').forEach((item) =>
          item.addEventListener('click', this.onItemClick.bind(this))
        );
      }

      getMenuDrawer() {
        return document.querySelector('.menu-drawer');
      }

      setPreventHide(val) {
        if (this.header) this.header.preventHide = !!val;
      }

      hidePanel() {
        // If there is no dropdown in this instance (flags-only), just ensure no errors.
        if (this.elements.button) this.elements.button.setAttribute('aria-expanded', 'false');
        if (this.elements.panel) this.elements.panel.setAttribute('hidden', true);

        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }

        document.body.classList.remove('overflow-hidden-mobile');

        const drawer = this.getMenuDrawer();
        if (drawer) drawer.classList.remove('country-selector-open');

        this.setPreventHide(false);
      }

      onContainerKeyDown(event) {
        // Only relevant for dropdown list navigation
        if (!this.elements.panel) return;

        const focusableItems = Array.from(this.querySelectorAll('[data-value]')).filter((item) => {
          const li = item.parentElement;
          return !(li && li.classList && li.classList.contains('hidden'));
        });

        if (!focusableItems.length) return;

        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch ((event.code || '').toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
          default:
            break;
        }

        if (!this.elements.search) return;

        // aria-activedescendant update
        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1 && focusableItems[focusedItemIndex].id) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        const code = (event.code || '').toUpperCase();
        if (!code) return;

        switch (code) {
          case 'ESCAPE': {
            // Only if dropdown exists and is open
            if (!this.elements.button) return;
            if (this.elements.button.getAttribute('aria-expanded') === 'false') return;
            event.preventDefault();
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          }
          case 'SPACE': {
            // Open only if dropdown exists
            if (!this.elements.button) return;
            event.preventDefault();
            if (this.elements.button.getAttribute('aria-expanded') === 'true') return;
            this.openSelector();
            break;
          }
          default:
            break;
        }
      }

      onItemClick(event) {
        // For dropdown items (usually <a data-value="..">)
        event.preventDefault();

        const form = this.querySelector('form');
        if (!form) return;

        const target = event.currentTarget;
        const value = target && target.dataset ? target.dataset.value : '';

        // Re-find input if needed (defensive)
        if (!this.elements.input) {
          this.elements.input = this.querySelector('input[name="locale_code"], input[name="country_code"]');
        }

        if (this.elements.input && value) {
          this.elements.input.value = value;
        }

        form.submit();
      }

      openSelector() {
        // If this instance is flags-only (no dropdown button/panel), do nothing.
        if (!this.elements.button || !this.elements.panel) return;

        this.elements.button.focus();

        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        );

        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }

        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }

        if (this.hasAttribute('data-prevent-hide')) {
          this.setPreventHide(true);
        }

        const drawer = this.getMenuDrawer();
        if (drawer) drawer.classList.add('country-selector-open');
      }

      closeSelector(event) {
        // If dropdown is not present, nothing to close.
        if (!this.elements.panel || !this.elements.button) return;

        const related = event.relatedTarget; // can be null
        const target = event.target;

        if (
          (target && target.classList && target.classList.contains('country-selector__overlay')) ||
          !this.contains(target) ||
          (related && !this.contains(related))
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return String(str || '')
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        if (!this.elements.search) return;

        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allItems = Array.from(this.querySelectorAll('[data-value]'));

        // Reset button
        if (this.elements.resetButton) {
          this.elements.resetButton.classList.toggle('hidden', !searchValue);
        }

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', !!searchValue);
        }

        let visibleCount = 0;

        allItems.forEach((item) => {
          const labelEl = item.querySelector('.country');
          const name = this.normalizeString(labelEl ? labelEl.textContent : item.textContent);
          const li = item.parentElement;

          const isMatch = name.indexOf(searchValue) > -1;
          if (li && li.classList) {
            li.classList.toggle('hidden', !isMatch);
          }
          if (isMatch) visibleCount += 1;
        });

        if (this.elements.liveRegion && window.accessibilityStrings && window.accessibilityStrings.countrySelectorSearchCount) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCount
          );
        }

        const scroller1 = this.querySelector('.country-selector');
        const scroller2 = this.querySelector('.country-selector__list');
        if (scroller1) scroller1.scrollTop = 0;
        if (scroller2) scroller2.scrollTop = 0;
      }

      resetFilter(event) {
        if (!this.elements.search) return;
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        if (this.elements.searchIcon) this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search || !this.elements.searchIcon) return;
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if ((event.code || '').toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
